import { useState, useRef } from 'react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthProvider';

type ReportType = 'msp-summary' | 'per-client' | 'combined' | 'month-end';
type DateRange = 'this-month' | 'last-month' | 'last-2-months' | 'last-3-months' | 'last-quarter' | 'last-6-months' | 'this-year' | 'custom';

interface ReportConfig {
  type: ReportType;
  dateRange: DateRange;
  customStartDate?: string;
  customEndDate?: string;
  selectedMonth?: number;
  selectedYear?: number;
}

const reportTypes: { value: ReportType; label: string; description: string }[] = [
  {
    value: 'msp-summary',
    label: 'MSP Summary Report',
    description: 'Organization-wide technician metrics across all clients'
  },
  {
    value: 'per-client',
    label: 'Per-Client Reports',
    description: 'Individual report for each client company'
  },
  {
    value: 'combined',
    label: 'Combined Report',
    description: 'MSP summary followed by per-company breakdown'
  },
  {
    value: 'month-end',
    label: 'Month End Export',
    description: 'ZIP file with per-client Excel files (TimeTech format)'
  }
];

const dateRanges: { value: DateRange; label: string }[] = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'last-2-months', label: 'Last 2 Months' },
  { value: 'last-3-months', label: 'Last 3 Months' },
  { value: 'last-quarter', label: 'Last Quarter' },
  { value: 'last-6-months', label: 'Last 6 Months' },
  { value: 'this-year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' }
];

// Generate month options for the past 12 months
function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();

  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    options.push({
      value: `${date.getFullYear()}-${date.getMonth() + 1}`,
      label: monthName,
    });
  }

  return options;
}

export default function Reports() {
  const { user } = useAuth();
  const now = new Date();
  const [config, setConfig] = useState<ReportConfig>({
    type: 'msp-summary',
    dateRange: 'this-month',
    selectedMonth: now.getMonth() + 1,
    selectedYear: now.getFullYear(),
  });
  const [loading, setLoading] = useState(false);
  const [reportHtml, setReportHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);
    setReportHtml(null);

    try {
      // Handle Month End export separately (downloads ZIP file)
      if (config.type === 'month-end') {
        const response = await fetch(`/api/reports/month-end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            year: config.selectedYear,
            month: config.selectedMonth,
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Failed to generate export: ${response.status}`);
        }

        // Check if response is JSON (error/info) or binary (ZIP)
        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          if (data.message) {
            setError(data.message);
          } else {
            throw new Error(data.error || 'Failed to generate export');
          }
        } else {
          // Download the ZIP file
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${config.selectedMonth}-${config.selectedYear} Month End.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
        return;
      }

      // Calculate date range for other report types
      const { startDate, endDate } = calculateDateRange(config.dateRange, config.customStartDate, config.customEndDate);

      const response = await fetch(`/api/reports/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: config.type,
          startDate,
          endDate
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate report: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.html) {
        setReportHtml(data.html);
      } else {
        throw new Error(data.error || 'Failed to generate report');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (reportRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>AutoTask Report</title>
            <style>
              @media print {
                body { margin: 0; padding: 20px; }
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>
            ${reportHtml}
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        userEmail={user?.email}
        middleContent={
          <div className="text-center">
            <span className="text-sm font-medium text-white">Reports</span>
            <span className="text-xs text-slate-500 ml-2">Technician Ticket Reports</span>
          </div>
        }
      />

      {/* Report Configuration */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <p className="text-sm text-slate-400 text-center mb-4">
          Generate and print technician performance reports
        </p>

        {/* Report Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Report Type
            </label>
            <div className="space-y-2">
              {reportTypes.map((rt) => (
                <label
                  key={rt.value}
                  className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                    config.type === rt.value
                      ? 'bg-blue-500/10 border-blue-500/50'
                      : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="reportType"
                    value={rt.value}
                    checked={config.type === rt.value}
                    onChange={(e) => setConfig({ ...config, type: e.target.value as ReportType })}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <span className="text-sm font-medium text-white">{rt.label}</span>
                    <p className="text-xs text-slate-400 mt-0.5">{rt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Date/Month Selection - conditional based on report type */}
          <div>
            {config.type === 'month-end' ? (
              <>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Select Month
                </label>
                <select
                  value={`${config.selectedYear}-${config.selectedMonth}`}
                  onChange={(e) => {
                    const [year, month] = e.target.value.split('-').map(Number);
                    setConfig({ ...config, selectedYear: year, selectedMonth: month });
                  }}
                  className="w-full p-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm mb-3"
                >
                  {getMonthOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>

                <div className="bg-slate-700/50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400">
                    <strong className="text-slate-300">Export Format:</strong> ZIP file containing one Excel workbook per client company.
                    Each workbook has 3 sheets: TimeDetails, TimeSummary, and ServiceDetails.
                  </p>
                </div>

                <button
                  onClick={handleGenerateReport}
                  disabled={loading}
                  className="w-full mt-4 py-2 px-4 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <span>📥</span>
                      Download Month End Export
                    </>
                  )}
                </button>
              </>
            ) : (
              <>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Date Range
                </label>
                <select
                  value={config.dateRange}
                  onChange={(e) => setConfig({ ...config, dateRange: e.target.value as DateRange })}
                  className="w-full p-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm mb-3"
                >
                  {dateRanges.map((dr) => (
                    <option key={dr.value} value={dr.value}>{dr.label}</option>
                  ))}
                </select>

                {config.dateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={config.customStartDate || ''}
                        onChange={(e) => setConfig({ ...config, customStartDate: e.target.value })}
                        className="w-full p-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">End Date</label>
                      <input
                        type="date"
                        value={config.customEndDate || ''}
                        onChange={(e) => setConfig({ ...config, customEndDate: e.target.value })}
                        className="w-full p-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleGenerateReport}
                  disabled={loading}
                  className="w-full mt-4 py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Report Output */}
      {reportHtml && (
        <div className="bg-white rounded-lg border border-slate-700 overflow-hidden">
          {/* Print Button Bar */}
          <div className="bg-slate-800 p-2 flex justify-end space-x-2 print:hidden">
            <button
              onClick={handlePrint}
              className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded transition-colors"
            >
              🖨️ Print / Save PDF
            </button>
          </div>

          {/* Report Content */}
          <div
            ref={reportRef}
            className="p-6"
            dangerouslySetInnerHTML={{ __html: reportHtml }}
          />
        </div>
      )}
    </div>
  );
}

function calculateDateRange(
  dateRange: DateRange,
  customStart?: string,
  customEnd?: string
): { startDate: string; endDate: string } {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0];
  let startDate: string;

  switch (dateRange) {
    case 'this-month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      break;
    case 'last-month': {
      // Full previous month — 1st of last month through last day of last month (NOT today).
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0); // day 0 of current month = last day of previous
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    }
    case 'last-2-months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      break;
    case 'last-3-months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().split('T')[0];
      break;
    case 'last-quarter':
      startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1).toISOString().split('T')[0];
      break;
    case 'last-6-months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1).toISOString().split('T')[0];
      break;
    case 'this-year':
      startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      break;
    case 'custom':
      startDate = customStart || endDate;
      return { startDate, endDate: customEnd || endDate };
    default:
      startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  }

  return { startDate, endDate };
}
